/* FENDO hero slider: video-aware playback, responsive media and seamless looping. */
(() => {
  if (customElements.get('fendo-hero-slider')) return;

  const TRANSITION_DELAY = 0;
  const MOBILE_MEDIA = '(max-width: 1199px) and (orientation: portrait)';

  class FendoHeroSlider extends HTMLElement {
    connectedCallback() {
      if (this.abort && !this.abort.signal.aborted) return;
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.track.querySelectorAll(':scope > [data-clone]').forEach(slide => slide.remove());
      this.slides = Array.from(this.track.children);
      this.count = this.slides.length;
      if (!this.count) return;

      this.bullets = Array.from(this.querySelectorAll('[data-bullet]'));
      this.pauseButton = this.querySelector('[data-pause]');
      this.interval = Math.max(1, Number(this.dataset.interval) || 6) * 1000;
      this.speed = 700;
      this.waitForVideo = this.dataset.videoEnd !== 'false';
      this.index = 0;
      this.busy = false;
      const bounds = this.getBoundingClientRect();
      this.inView = bounds.bottom > 0 && bounds.top < window.innerHeight;
      this.activeVideo = null;
      this.failedVideo = null;
      this.playRequest = null;
      this.pendingSeek = null;
      this.remaining = this.interval;
      this.timerStartedAt = null;
      this.mediaQuery = matchMedia(MOBILE_MEDIA);
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.dataset.autoplay !== 'true';
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.style.setProperty('--fhs-speed', `${this.speed}ms`);
      this.classList.remove('is-transitioning');
      this.setProgress(0);

      Object.assign(this.track.style, { display: 'grid', gap: '0', transform: 'none', position: 'relative', zIndex: '0', willChange: 'auto' });
      this.slides.forEach(slide => {
        Object.assign(slide.style, { gridArea: '1 / 1', width: '100%', opacity: '0', pointerEvents: 'none' });
      });
      this.setPosition(0, false);

      this.bullets.forEach((bullet, index) => {
        bullet.addEventListener('click', () => {
          this.goTo(index);
        }, { signal });
      });
      this.pauseButton?.addEventListener('click', () => this.setPaused(!this.userPaused), { signal });
      this.track.addEventListener('transitionend', event => {
        if (event.target === this.slides[this.pendingTarget] && event.propertyName === 'opacity') this.finish();
      }, { signal });
      this.setupSwipe(signal);

      this.slides.forEach(slide => slide.querySelectorAll('video').forEach(video => {
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.preload = 'none';
        video.addEventListener('ended', () => {
          if (video === this.activeVideo && this.waitForVideo && this.canPlay() && this.count > 1) this.next();
        }, { signal });
        video.addEventListener('timeupdate', () => {
          if (video !== this.activeVideo) return;
          if (this.waitForVideo) this.updateVideoProgress(video);
          if (!video.paused && !video.ended) this.armVideoWatchdog(video, 20000);
        }, { signal });
        video.addEventListener('loadedmetadata', () => {
          this.applyPendingSeek(video);
          if (video === this.activeVideo && this.waitForVideo) this.updateVideoProgress(video);
        }, { signal });
        video.addEventListener('playing', () => this.armVideoWatchdog(video, 20000), { signal });
        video.addEventListener('error', () => this.videoFailed(video), { signal });
      }));

      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.refreshPlayback();
      });
      this.observer.observe(this);
      document.addEventListener('visibilitychange', () => this.refreshPlayback(), { signal });
      window.addEventListener('pageshow', () => this.refreshPlayback(), { signal });
      this.mediaQuery.addEventListener('change', () => this.refreshPlayback(), { signal });
      this.motionQuery.addEventListener('change', () => {
        if (this.motionQuery.matches && this.busy) this.finish();
      }, { signal });
      this.addEventListener('focusin', event => {
        if (event.target.matches(':focus-visible')) this.setPaused(true);
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        const index = this.slides.indexOf(event.target.closest('.fhs__slide'));
        if (index < 0) return;
        this.setPaused(true);
        this.goTo(index, true);
      }, { signal });
      this.addEventListener('shopify:block:deselect', () => {
        this.setPaused(this.dataset.autoplay !== 'true');
      }, { signal });
      this.updateState();

      // Hold video playback until the page has loaded, so the hero video does not compete with
      // the LCP image for bandwidth. The image underneath acts as the poster meanwhile.
      // A timeout and the Theme Editor release the hold, so playback can never stay blocked.
      this.mediaReady = document.readyState === 'complete' || Boolean(window.Shopify?.designMode);
      if (!this.mediaReady) {
        const release = () => {
          if (this.mediaReady) return;
          this.mediaReady = true;
          this.upgradeVideos();
          this.refreshPlayback();
        };
        window.addEventListener('load', () => setTimeout(release, 300), { once: true, signal });
        setTimeout(release, 6000);
      } else {
        this.upgradeVideos();
      }

      this.refreshPlayback();
    }

    // Large, fast screens (e.g. 4K / QHD monitors) get the HD rendition (data-hd-src) before
    // playback starts. Everyone else, and the mobile video, keeps the lighter default source.
    upgradeVideos() {
      if (this.upgraded) return;
      this.upgraded = true;
      const connection = navigator.connection;
      const slow = connection?.saveData === true || /(^|-)(2g|3g)$/.test(connection?.effectiveType || '');
      const physicalWidth = window.innerWidth * (window.devicePixelRatio || 1);
      if (slow || physicalWidth < 1600) return;
      this.track.querySelectorAll('video[data-hd-src]').forEach(video => {
        if (video.readyState !== 0) return;
        const source = video.querySelector('source');
        if (!source || source.getAttribute('src') === video.dataset.hdSrc) return;
        source.setAttribute('src', video.dataset.hdSrc);
        // The browser has already picked the first source (preload="none" only defers the download),
        // so the resource selection must be restarted. Nothing is downloaded until play().
        video.load();
      });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.clearPlaybackTimers();
      clearTimeout(this.slideTimer);
      clearTimeout(this.arriveTimer);
      this.playRequest = null;
      this.track?.querySelectorAll('video').forEach(video => video.pause());
      this.track?.querySelectorAll(':scope > [data-clone]').forEach(slide => slide.remove());
    }

    makeClone(slide) {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.dataset.clone = '';
      clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
      clone.querySelectorAll('img').forEach(image => {
        image.loading = 'lazy';
        image.removeAttribute('fetchpriority');
      });
      clone.querySelectorAll('video').forEach(video => {
        video.removeAttribute('autoplay');
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.remove());
        video.preload = 'none';
      });
      return clone;
    }

    setPosition(index, animate) {
      const active = ((index % this.count) + this.count) % this.count;
      this.slides.forEach((slide, i) => {
        slide.style.transition = animate ? `opacity ${this.motionQuery.matches ? 150 : this.speed}ms ease-in-out` : 'none';
        slide.style.opacity = i === active ? '1' : '0';
        slide.style.zIndex = i === active ? '1' : '0';
        slide.style.pointerEvents = i === active ? 'auto' : 'none';
      });
      if (!animate) void this.track.offsetWidth;
    }

    goTo(target, instant = false) {
      if (this.count < 2 || (target === this.index && !this.busy) || (this.busy && !instant)) return;
      this.clearPlaybackTimers();
      this.track.querySelectorAll('video').forEach(video => video.pause());
      this.playRequest = null;
      clearTimeout(this.slideTimer);
      clearTimeout(this.arriveTimer);
      if (instant) {
        this.index = ((target % this.count) + this.count) % this.count;
        this.busy = false;
        this.classList.remove('is-transitioning');
        this.setPosition(this.index, false);
        this.resetSlide();
        return;
      }
      this.busy = true;
      this.pendingTarget = ((target % this.count) + this.count) % this.count;
      this.slideTimer = setTimeout(() => this.setPosition(target, true), TRANSITION_DELAY);
      this.arriveTimer = setTimeout(() => this.finish(), TRANSITION_DELAY + this.speed + 200);
    }

    next() { this.goTo(this.index + 1); }
    prev() { this.goTo(this.index - 1); }

    finish() {
      if (!this.busy) return;
      clearTimeout(this.slideTimer);
      clearTimeout(this.arriveTimer);
      this.index = ((this.pendingTarget % this.count) + this.count) % this.count;
      this.setPosition(this.index, false);
      this.busy = false;
      this.classList.remove('is-transitioning');
      this.resetSlide();
    }

    resetSlide() {
      this.activeVideo = null;
      this.failedVideo = null;
      this.playRequest = null;
      this.pendingSeek = null;
      this.remaining = this.interval;
      this.setProgress(0);
      this.slides[this.index].querySelectorAll('video').forEach(video => {
        try { video.currentTime = 0; } catch (_) { /* Metadata is not available yet. */ }
      });
      this.updateState();
      this.refreshPlayback();
    }

    updateState() {
      this.slides.forEach((slide, index) => {
        slide.inert = index !== this.index;
        slide.setAttribute('aria-hidden', String(index !== this.index));
      });
      this.bullets.forEach((bullet, index) => bullet.setAttribute('aria-current', String(index === this.index)));
    }

    setPaused(paused) {
      this.userPaused = paused;
      if (!paused) this.failedVideo = null;
      this.refreshPlayback();
    }

    canPlay() {
      return this.isConnected && this.inView && document.visibilityState === 'visible' && !this.userPaused && !this.busy;
    }

    clearPlaybackTimers() {
      clearTimeout(this.timer);
      clearTimeout(this.videoWatchdog);
      cancelAnimationFrame(this.progressFrame);
      if (this.timerStartedAt != null) {
        this.remaining = Math.max(0, this.remaining - (performance.now() - this.timerStartedAt));
        this.timerStartedAt = null;
      }
    }

    refreshPlayback() {
      if (!this.count) return;
      this.clearPlaybackTimers();
      this.classList.toggle('is-paused', this.userPaused);
      if (this.pauseButton) {
        this.pauseButton.setAttribute('aria-label', this.userPaused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
      }
      const video = Array.from(this.slides[this.index].querySelectorAll('video')).find(item => getComputedStyle(item).display !== 'none') || null;
      const previous = this.activeVideo;
      if (video !== previous) {
        this.activeVideo = video;
        this.playRequest = null;
        if (video && previous && Number.isFinite(previous.duration) && previous.duration > 0) {
          this.pendingSeek = { video, fraction: Math.min(0.999, previous.currentTime / previous.duration) };
          this.applyPendingSeek(video);
        }
      }
      this.track.querySelectorAll('video').forEach(item => {
        item.autoplay = this.mediaReady && item === video && this.canPlay() && item !== this.failedVideo;
        if (item !== video || !this.canPlay()) item.pause();
      });
      if (!this.canPlay()) {
        this.playRequest = null;
        return;
      }

      if (video && !this.mediaReady) {
        this.playRequest = null;
        return;
      }

      if (video && video !== this.failedVideo) {
        video.loop = this.count === 1 || !this.waitForVideo;
        video.preload = 'auto';
        if (video.ended) {
          try { video.currentTime = 0; } catch (_) { /* Retain the poster until ready. */ }
        }
        if (this.waitForVideo) this.updateVideoProgress(video);
        this.armVideoWatchdog(video, 12000);
        if (video.paused && !this.playRequest) {
          const token = { video };
          this.playRequest = token;
          video.play().then(() => {
            if (this.playRequest === token) this.playRequest = null;
            if (video !== this.activeVideo || !this.canPlay()) video.pause();
          }).catch(error => {
            if (this.playRequest !== token) return;
            this.playRequest = null;
            if (error.name === 'NotAllowedError') this.setPaused(true);
            else if (error.name === 'AbortError') this.setPaused(true);
            else this.videoFailed(video);
          });
        }
        if (this.waitForVideo) return;
      }
      if (this.count > 1) this.startImageTimer();
    }

    applyPendingSeek(video) {
      if (this.pendingSeek?.video !== video || !Number.isFinite(video.duration) || video.duration <= 0) return;
      try {
        video.currentTime = this.pendingSeek.fraction * video.duration;
        this.pendingSeek = null;
      } catch (_) { /* Retry after metadata loads. */ }
    }

    videoFailed(video) {
      if (video !== this.activeVideo || !this.canPlay()) return;
      this.failedVideo = video;
      video.pause();
      this.playRequest = null;
      if (this.count === 1) this.userPaused = true;
      this.refreshPlayback();
    }

    armVideoWatchdog(video, delay) {
      if (video !== this.activeVideo || video === this.failedVideo || !this.canPlay()) return;
      clearTimeout(this.videoWatchdog);
      this.videoWatchdog = setTimeout(() => this.videoFailed(video), delay);
    }

    startImageTimer() {
      this.timerStartedAt = performance.now();
      this.timer = setTimeout(() => {
        this.timerStartedAt = null;
        this.remaining = 0;
        this.next();
      }, this.remaining);
      const tick = () => {
        const elapsed = performance.now() - this.timerStartedAt;
        this.setProgress(1 - Math.max(0, this.remaining - elapsed) / this.interval);
        this.progressFrame = requestAnimationFrame(tick);
      };
      this.progressFrame = requestAnimationFrame(tick);
    }

    updateVideoProgress(video) {
      this.setProgress(Number.isFinite(video.duration) && video.duration > 0 ? video.currentTime / video.duration : 0);
    }

    setProgress(progress) {
      this.style.setProperty('--fhs-progress', `${Math.max(0, Math.min(1, progress)) * 100}%`);
    }

    setupSwipe(signal) {
      let startX = 0;
      let startY = 0;
      let tracking = false;
      let blockClickUntil = 0;
      this.track.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse' || event.target.closest('button, input, select, textarea')) return;
        startX = event.clientX;
        startY = event.clientY;
        tracking = true;
      }, { signal });
      this.track.addEventListener('pointerup', event => {
        if (!tracking) return;
        tracking = false;
        const dx = event.clientX - startX;
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(event.clientY - startY)) return;
        blockClickUntil = performance.now() + 400;
        dx < 0 ? this.next() : this.prev();
      }, { signal });
      this.track.addEventListener('pointercancel', () => { tracking = false; }, { signal });
      this.track.addEventListener('click', event => {
        if (performance.now() >= blockClickUntil) return;
        event.preventDefault();
        event.stopPropagation();
      }, { signal, capture: true });
    }
  }

  customElements.define('fendo-hero-slider', FendoHeroSlider);
})();
